/**
 * The 55 event types, as this context's OWN union.
 *
 * ==========================================================================================
 * WHY A COPY, WHEN THREE BETTER HOMES SEEM TO EXIST
 * ==========================================================================================
 *
 * Each of the three is closed, and it is worth knowing which door you are trying:
 *
 *   1. **Another context's constants.** `audit` may not import `@gladis/domain-*` — bounded contexts
 *      never import each other, and the reader would have to import all six to assemble the set.
 *   2. **`@gladis/db`'s generated `ActivityLogEventType`.** Banned by name in
 *      `tooling/eslint/domain.js`: "Bounded contexts are infrastructure-agnostic." The union that would
 *      make this list a compile-time fact is on the other side of the boundary this package exists to
 *      keep.
 *   3. **`@gladis/shared`.** `domain.md` §7 names activity event names in the list of what does NOT go
 *      in the shared kernel, because a union of every writer's events couples all six through one type
 *      that changes whenever any of them adds an event.
 *
 * So it is a copy, deliberately, and a copy is only safe if something checks it.
 * `apps/api/src/__tests__/architecture/activity-catalogue.test.ts` asserts set equality against the
 * database enum **in both directions** — every enum value present here, and nothing here that the enum
 * does not have. That file lives in `apps/api` because it is the only place allowed to see both sides.
 *
 * ORDER IS BY MODULE, NOT ALPHABETICAL. The set-equality test does not care, and grouping is what makes
 * a missing event visible to a person reading the list against `activity-log.md`'s tables.
 */
export const ACTIVITY_EVENT_TYPES = [
  // Household and membership — identity, features 003 and 005
  "member.invited",
  "member.joined",
  "member.removed",
  "member.left",
  "household.created",
  "household.renamed",
  "household.ownership_transferred",
  /**
   * IN THE ENUM AND NEVER WRITTEN.
   *
   * Every household-scoped table cascades from `households` and `household_id` is `NOT NULL`, so an
   * entry recording a household's deletion is destroyed by the statement that writes it. 005's FR-041
   * settled this: the deletion goes to the OPERATIONAL log instead. `IDENTITY_ACTIVITY_EVENTS` omits it
   * for that reason.
   *
   * It is listed here regardless, because this list must equal the enum — the column can hold the value,
   * so the reader must be able to render it. A label for an event that cannot occur costs one line; a
   * missing label for one that can is a raw enum value on someone's screen.
   */
  "household.deleted",

  // Expenses — feature 009
  "expense.created",
  "expense.updated",
  "expense.archived",
  "expense.unarchived",
  /**
   * The two attribution events, whose `entity_type` is `mortgage` rather than `expense`.
   *
   * They are `expense.*` events about a DERIVED line — the mortgage interest that appears among the
   * expenses without an `expenses` row behind it. There is no expense to open, which is why the
   * destination mapping keys on `entity_type` and not on the event name's prefix.
   */
  "expense.attribution_set",
  "expense.attribution_cleared",
  "expense.payment_date_set",
  "expense.payment_date_cleared",

  // Cards — feature 009
  "card.created",
  "card.updated",
  "card.archived",
  "card.unarchived",

  // Income — feature 015
  "income.source_created",
  "income.source_updated",
  "income.amount_recorded",
  "income.amount_corrected",
  /** Ending and archiving are different facts about the past. Four events, not two. */
  "income.source_ended",
  "income.source_resumed",
  "income.source_archived",
  "income.source_unarchived",
  "income.entry_recorded",
  "income.entry_updated",
  "income.entry_deleted",

  // Savings — feature 006
  "savings.account_created",
  "savings.balance_updated",
  "savings.account_archived",
  "savings.account_unarchived",

  // Investments — feature 016, and 011's fetch
  "investment.stock_added",
  "investment.stock_updated",
  "investment.stock_removed",
  "investment.transaction_recorded",
  "investment.transaction_updated",
  "investment.transaction_deleted",
  "investment.price_updated",
  "investment.price_deleted",
  /** One row per RUN, with no `entity_id`. Written by the scheduled fetch, which is 017's. */
  "investment.prices_fetched",
  /** FAILURES ONLY, one row per stock that could not be priced. There is no success form. */
  "investment.price_fetched",

  // Properties and mortgages — feature 008
  "property.created",
  "property.updated",
  "property.valuation_added",
  "property.valuation_fetched",
  "property.sold",
  "property.archived",
  "property.unarchived",
  "mortgage.added",
  "mortgage.updated",
  "mortgage.refinanced",
  "mortgage.archived",
  "mortgage.unarchived",

  // Vehicles — feature 023
  //
  // FIVE OF THESE ARE `property.*` ONE MODULE ALONG, and that is deliberate rather than lazy: a
  // property and a vehicle are the same shape of thing — owned, valued occasionally by a person,
  // archived when the record was a mistake, and SOLD when it genuinely leaves. A feed saying "sold"
  // for a house and "disposed" for a car would invite the reader to look for a difference that does
  // not exist.
  "vehicle.created",
  "vehicle.updated",
  "vehicle.archived",
  "vehicle.unarchived",
  "vehicle.sold",
  "vehicle.valuation_added",
  "vehicle.valuation_updated",
  "vehicle.valuation_deleted",
  /**
   * Linking a cost MOVES A FIGURE ON A PAGE READ MONTHLY — four rows become one, and the row that
   * vanished is not gone. Somebody hunting a missing expense should find out here what happened to
   * it, which is the same service the feed performs for an archived one.
   *
   * Both carry the VEHICLE's id: a link has no page of its own, and the vehicle is where a reader
   * wants to arrive.
   */
  "vehicle.cost_linked",
  "vehicle.cost_unlinked",
] as const;

export type ActivityEventType = (typeof ACTIVITY_EVENT_TYPES)[number];

/**
 * The 14 entity types, on the same terms and for the same reasons.
 *
 * `entity_type` is `NOT NULL`, so every row carries one of these. What may be absent is `entity_id`,
 * and that absence — not the entity type — is what decides whether a row is activatable.
 */
export const ACTIVITY_ENTITY_TYPES = [
  "expense",
  "card",
  "income_source",
  "income_source_amount",
  "income_entry",
  "savings_account",
  "stock",
  "stock_transaction",
  "property",
  "mortgage",
  "member",
  "household",
  "vehicle",
  /**
   * Added by 023, and it resolves to a MODULE rather than a record.
   *
   * A valuation has no page of its own, so giving it a detail destination would oblige the feed to
   * resolve one that does not exist. `destinations.ts` sends it to the vehicles module — the rule
   * that "a destination is a place, not necessarily a record".
   */
  "vehicle_valuation",
] as const;

export type ActivityEntityType = (typeof ACTIVITY_ENTITY_TYPES)[number];
